import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { T, useT } from "@repo/transifex";
import { Dialog, DialogError, DialogLoading } from "~/components";
import { useSendWebEvent } from "~/hooks";
import { useAppContext, useAppDispatch } from "~/providers/app";
import type {
  Scale,
  ScaleBoundUnit,
  ScaleData,
  ScaleModel
} from "~/types/hardware";

import { ScaleConnectionStatus } from "./scale-connection-status";
import { ScalePairingForm } from "./scale-pairing-form";
import { ScalePrecisionFlow } from "./scale-precision-flow";
import { useScaleTrial, type ScaleTrialPhase } from "./use-scale-trial";

const isScaleWithModel = (
  scale: Scale
): scale is { model: string; scale: ScaleData } =>
  "model" in scale && "scale" in scale;

const isFTDI = (vendorId: string) => {
  return String(vendorId) === "1027";
};

const ScaleButton = ({
  scale,
  onScaleSelect
}: {
  scale: Scale;
  onScaleSelect: (scale: Scale) => void;
}) => {
  let vendorId;
  if (isScaleWithModel(scale)) {
    vendorId = scale.scale.vendorId;
  } else if (isFTDI(scale.vendorId)) {
    vendorId = "FTDI";
  } else {
    vendorId = scale.vendorId;
  }

  return (
    <button
      className="border-y p-4 text-left hover:bg-gray-100"
      onClick={() => {
        onScaleSelect(scale);
      }}
      type="button"
    >
      {isScaleWithModel(scale) ? scale.scale.deviceId : scale.deviceId} -{" "}
      {vendorId} -{" "}
      {isScaleWithModel(scale) ? scale.scale.productId : scale.productId}
    </button>
  );
};

/** The skip path only waits for the scale to answer, so "waiting" already counts as connected. */
const connectionPhase = (
  phase: ScaleTrialPhase
): "connecting" | "connected" | "error" => {
  if (phase === "error") return "error";
  if (phase === "connecting") return "connecting";
  return "connected";
};

type SkipPathProps = {
  scale: Scale;
  model: ScaleModel;
  unit: ScaleBoundUnit;
  onDone: () => void;
};

/** Pairing is saved; open the scale once so the operator knows it answers. */
const ScaleSkipPath = ({ scale, model, unit, onDone }: SkipPathProps) => {
  const trial = useScaleTrial({
    scale,
    model,
    unit,
    decimals: null,
    waitFor: "reading"
  });

  return (
    <ScaleConnectionStatus
      onCancel={onDone}
      onDone={onDone}
      onRetry={trial.retry}
      phase={connectionPhase(trial.phase)}
      reading={trial.reading}
    />
  );
};

type PairingStep = "pick" | "connect" | "precision";

const ScalesPage = () => {
  const {
    hardware: {
      scales: {
        discovery: { devices: scales, status }
      }
    }
  } = useAppContext();

  const dispatch = useAppDispatch();
  const sendWebEvent = useSendWebEvent();
  const navigate = useNavigate();
  const t = useT();

  const [selectedScale, setSelectedScale] = useState<Scale | undefined>();
  const [model, setModel] = useState<ScaleModel | null>(null);
  const [unit, setUnit] = useState<ScaleBoundUnit>("g");
  const [step, setStep] = useState<PairingStep>("pick");

  const handleDiscoveryReset = () => {
    dispatch({ type: "SCALES_DISCOVER_RESET" });
    setSelectedScale(undefined);
    setModel(null);
    setUnit("g");
    setStep("pick");
  };

  const finish = () => {
    handleDiscoveryReset();
    navigate("..");
  };

  const savePairing = (
    scale: Scale,
    chosenModel: ScaleModel,
    decimals: number | null
  ) => {
    dispatch({
      type: "SCALE_PAIRING_SELECTED",
      payload: { model: chosenModel, unit, decimals }
    });
    sendWebEvent({
      type: "SCALE_SELECT_REQUEST",
      payload: { scale, model: chosenModel, unit, decimals }
    });
  };

  if (status === "loading" || status === "idle")
    return <DialogLoading backUrl=".." onOutsideClick={handleDiscoveryReset} />;

  if (status === "error" || !scales.length)
    return (
      <DialogError
        backUrl=".."
        error={t("No Scales found")}
        onOutsideClick={handleDiscoveryReset}
      />
    );

  if (!selectedScale) {
    return (
      <Dialog backUrl=".." onOutsideClick={handleDiscoveryReset} size="md">
        <div className="flex w-full flex-col p-8">
          <p className="mx-auto mb-16 text-3xl">
            <T _str="Choose Scale" />
          </p>

          {scales.map((scale) => (
            <ScaleButton
              key={"model" in scale ? scale.scale.deviceId : scale.deviceId}
              onScaleSelect={setSelectedScale}
              scale={scale}
            />
          ))}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog backUrl=".." onOutsideClick={handleDiscoveryReset} size="md">
      <div className="flex w-full flex-col p-8">
        {step === "pick" ? (
          <ScalePairingForm
            model={model}
            onConfigurePrecision={() => {
              setStep("precision");
            }}
            onConnect={() => {
              if (!model) return;
              // Connecting without a precision declares the driver's default
              // and clears whatever an earlier pairing stored.
              savePairing(selectedScale, model, null);
              setStep("connect");
            }}
            onModelChange={setModel}
            onUnitChange={setUnit}
            unit={unit}
          />
        ) : null}

        {step === "connect" && model ? (
          <ScaleSkipPath
            model={model}
            onDone={finish}
            scale={selectedScale}
            unit={unit}
          />
        ) : null}

        {step === "precision" && model ? (
          <ScalePrecisionFlow
            initialDecimals={null}
            model={model}
            onBack={() => {
              setStep("pick");
            }}
            onConfirm={(decimals) => {
              savePairing(selectedScale, model, decimals);
            }}
            onDone={finish}
            scale={selectedScale}
            unit={unit}
          />
        ) : null}
      </div>
    </Dialog>
  );
};

export { ScalesPage };
